import { useState, type FormEvent } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";

import { AuthShell, authButton, authInput } from "@/components/AuthShell";
import { errorMessage } from "@/lib/errors";
import { useForgotPassword } from "@/lib/password";

/**
 * "Esqueci minha senha" — vale para cliente, equipe e parceiro.
 *
 * A resposta é a mesma exista ou não a conta: a tela não pode servir para
 * descobrir quem tem cadastro.
 */
export const Route = createFileRoute("/esqueci-senha")({
  head: () => ({ meta: [{ title: "Esqueci minha senha — MonteBox" }] }),
  validateSearch: (search: Record<string, unknown>): { de?: "painel" } =>
    search["de"] === "painel" ? { de: "painel" } : {},
  component: ForgotPassword,
});

function ForgotPassword() {
  const { de } = Route.useSearch();
  const forgot = useForgotPassword();
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const back = de === "painel" ? "/admin/entrar" : "/minha-conta/entrar";

  const submit = (event: FormEvent) => {
    event.preventDefault();
    forgot.mutate(email.trim(), { onSuccess: () => setSentTo(email.trim()) });
  };

  if (sentTo) {
    return (
      <AuthShell
        title="Confira seu e-mail"
        subtitle={
          <>
            Se houver uma conta com <strong className="text-mb-ink">{sentTo}</strong>, enviamos um
            link para criar uma senha nova. Ele vale por 1 hora.
          </>
        }
      >
        <div className="flex items-start gap-3 rounded-mb-field bg-mb-surface px-4 py-3 text-xs leading-5 text-mb-muted">
          <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-mb-navy" />
          Não chegou em alguns minutos? Olhe a caixa de spam ou tente de novo com o e-mail que você
          usa para entrar.
        </div>
        <Link to={back} className={`${authButton} mt-6`}>
          Voltar para entrar
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Esqueci minha senha"
      subtitle="Informe o e-mail da sua conta. Enviamos um link para você criar uma senha nova."
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        <label className="block text-sm font-bold text-mb-ink">
          E-mail
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="voce@exemplo.com.br"
            className={authInput}
          />
        </label>
        {forgot.isError && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage(forgot.error)}
          </p>
        )}
        <button type="submit" disabled={forgot.isPending} className={authButton}>
          {forgot.isPending && <Loader2 className="h-5 w-5 animate-spin" />}
          Enviar link
        </button>
        <Link
          to={back}
          className="flex items-center justify-center gap-1.5 text-sm font-semibold text-mb-navy hover:text-mb-ink"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para entrar
        </Link>
      </form>
    </AuthShell>
  );
}
